// src/pages/Project/ProjectsRecentlyDeleted.tsx
// Soft-deleted projects bin. Lists projects with `deleted_at != null`,
// supports date-range filtering, and lets the user either restore or
// permanently delete each row.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Trash2, RotateCcw, Search, AlertTriangle,
} from 'lucide-react';
import { projectsApi, Project } from '../../api/projects';
import ConfirmDialog, { type ConfirmState } from '../../components/ConfirmDialog';
import { DateField } from '../../components/ui/DateField';
import { toast } from "sonner";

interface DeletedProject extends Project {
  deleted_at?: string | null;
}

export default function ProjectsRecentlyDeleted() {
  const navigate = useNavigate();
  const [items, setItems] = useState<DeletedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Per-row action state to disable buttons while their request is in flight.
  const [busyId, setBusyId] = useState<string | null>(null);
  // In-app confirmation dialog (replaces native window.confirm).
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await projectsApi.listDeleted({
        search: search.trim() || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        page_size: 200,
      });
      setItems((res.data?.items as DeletedProject[]) || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load recently deleted projects');
    } finally {
      setLoading(false);
    }
  }, [search, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRestore = (p: DeletedProject) => {
    setConfirm({
      title: 'Restore project?',
      description: <>Project <strong>"{p.name}"</strong> will be moved back to active projects.</>,
      confirmLabel: 'Restore',
      tone: 'success',
      onConfirm: async () => {
        setBusyId(p.id);
        try {
          await projectsApi.restore(p.id);
          await load();
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || 'Failed to restore');
          throw e;
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  const handleHardDelete = (p: DeletedProject) => {
    setConfirm({
      title: 'Delete forever?',
      description: <>Project <strong>"{p.name}"</strong> will be permanently removed. <strong>This cannot be undone.</strong></>,
      confirmLabel: 'Delete forever',
      tone: 'danger',
      onConfirm: async () => {
        setBusyId(p.id);
        try {
          await projectsApi.deletePermanent(p.id);
          await load();
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || 'Failed to delete permanently');
          throw e;
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  const clearFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
  };

  const filteredCount = useMemo(() => items.length, [items]);

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-6xl mx-auto p-4 md:p-7 space-y-5">
        <button
          onClick={() => navigate('/projects')}
          className="gt-btn is-ghost is-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Projects
        </button>

        {/* Header */}
        <div className="gt-page-hero flex-col md:flex-row gap-4">
          <div>
            <h1 className="gt-page-title">
              Recently <em>Deleted</em> Projects
            </h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Restore a project to bring it back, or delete it permanently.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="gt-card p-4 flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Search</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or code…"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
            </div>
          </div>
          <div className="min-w-[170px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Deleted from</label>
            <DateField
              value={dateFrom}
              onChange={(v) => setDateFrom(v || '')}
              placeholder="Pick a date"
            />
          </div>
          <div className="min-w-[170px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Deleted to</label>
            <DateField
              value={dateTo}
              onChange={(v) => setDateTo(v || '')}
              placeholder="Pick a date"
            />
          </div>
          {(search || dateFrom || dateTo) && (
            <button
              type="button"
              onClick={clearFilters}
              className="gt-btn is-ghost is-sm"
            >
              Clear
            </button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="gt-loading-ring" />
          </div>
        ) : error ? (
          <div className="gt-card p-8 text-center" style={{ borderColor: 'rgba(220,38,38,0.18)' }}>
            <AlertTriangle className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--gt-dnd)' }} />
            <p className="gt-page-sub">{error}</p>
            <button onClick={load} className="gt-btn is-danger mt-4 mx-auto">
              Try Again
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="gt-card p-12 text-center">
            <Trash2 className="w-10 h-10 mx-auto mb-3 text-gray-400" />
            <h3 className="gt-display" style={{ fontSize: 16, color: 'var(--gt-text-primary)' }}>
              The bin is empty
            </h3>
            <p className="gt-page-sub mt-2">
              {(search || dateFrom || dateTo)
                ? 'No deleted projects match these filters.'
                : 'Deleted projects show up here for review before they are permanently removed.'}
            </p>
          </div>
        ) : (
          <>
            <p className="gt-page-sub gt-numerals">
              Showing <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{filteredCount}</span> deleted project{filteredCount === 1 ? '' : 's'}.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((p) => (
                <div key={p.id} className="gt-card p-4 flex flex-col gap-3" style={{ opacity: busyId === p.id ? 0.6 : 1 }}>
                  <div>
                    <h3 className="gt-display" style={{ fontSize: 15, color: 'var(--gt-text-primary)' }}>{p.name}</h3>
                    {p.code && (
                      <p className="text-[11px] uppercase tracking-wide text-gray-500 mt-0.5">{p.code}</p>
                    )}
                    {p.description && (
                      <p className="text-xs text-gray-600 mt-2 line-clamp-2">{p.description}</p>
                    )}
                  </div>

                  <div className="text-[11px] text-gray-500 space-y-0.5">
                    {(() => {
                      // Projects can name several clients; the singular field is
                      // only the first of them.
                      const names = (p.client_names || []).filter(Boolean);
                      if (!names.length) return null;
                      return (
                        <p>
                          {names.length === 1 ? 'Client' : 'Clients'}:{' '}
                          <span className="text-gray-700">{names.join(', ')}</span>
                        </p>
                      );
                    })()}
                    {p.deleted_at && (
                      <p>Deleted: <span className="text-gray-700 gt-numerals">{new Date(p.deleted_at).toLocaleString()}</span></p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => handleRestore(p)}
                      disabled={busyId === p.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors disabled:opacity-50"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Restore
                    </button>
                    <button
                      onClick={() => handleHardDelete(p)}
                      disabled={busyId === p.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-50 ml-auto"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete forever
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* In-app confirmation dialog (replaces native window.confirm). */}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
